import React from "react";
import html2canvas from "html2canvas";
import { pdf, Document, Page, View, Text, Image, StyleSheet, Font } from "@react-pdf/renderer";
import CenturyGothicFont from "Assets/fonts/centurygothic.ttf";
import CenturyGothicFontBold from "Assets/fonts/centurygothic_bold.ttf";
import { SB_COLORS } from "theme/SbThemeV2";
import {
  getLegendStatuses, BASELINE_TAG, getRoleChangeConfig, getRoleCountCards, formatUsd, getBudgetOverview, getBudgetVarianceNote,
} from "../orgDesign.constants";
import { getHeadcountBridge } from "../orgDesignTreeEngine";

// Same family/files every other react-pdf export in the app registers.
Font.register({
  family: "CenturyGothic",
  fonts: [
    { src: CenturyGothicFont, fontWeight: "normal" },
    { src: CenturyGothicFontBold, fontWeight: "bold" },
  ],
});

// A4 landscape in PDF points.
const PAGE = { width: 841.89, height: 595.28 };
const MARGIN = { top: 30, side: 36, bottom: 44 };
// Space left for a tree once the running header (and the Proposed page's
// legend row) are laid out — every tree is scaled to fit inside this box so
// each structure lands on exactly one page, however large the org.
// (Width also leaves room for treeFrame's own padding + border.)
const TREE_BOX = { width: PAGE.width - MARGIN.side * 2 - 20, height: 400 };

// Browsers refuse canvases past ~16k px per side (and cap total area —
// Safari lowest); exceeding either silently yields a BLANK canvas, so a
// very large org must be captured below 1x rather than blow the limit.
const MAX_CANVAS_SIDE = 12000;
const MAX_CANVAS_AREA = 60_000_000;
const MAX_CAPTURE_SCALE = 3;

const FOOTER_TEXT = "SkillsBridge.AI Inc. — Confidential Organisation Design";

const C = {
  primary: SB_COLORS.primary,
  dark: SB_COLORS.dark,
  muted: SB_COLORS.gray,
  faint: SB_COLORS.tertiary,
  border: SB_COLORS.borderLight,
  panel: "#F8FAFC",
  success: SB_COLORS.success,
  danger: SB_COLORS.danger,
  warning: "#EA580C",
};

const styles = StyleSheet.create({
  page: {
    paddingTop: MARGIN.top,
    paddingHorizontal: MARGIN.side,
    paddingBottom: MARGIN.bottom,
    fontFamily: "CenturyGothic",
    fontSize: 10,
    color: C.dark,
    backgroundColor: "#FFFFFF",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-end",
    paddingBottom: 10,
    marginBottom: 14,
    borderBottomWidth: 1,
    borderBottomColor: C.border,
  },
  eyebrow: { fontSize: 7.5, fontWeight: "bold", letterSpacing: 1, color: C.primary, marginBottom: 4 },
  title: { fontSize: 18, fontWeight: "bold", color: C.dark },
  subtitle: { fontSize: 9, color: C.muted, marginTop: 3 },
  headerMeta: { alignItems: "flex-end" },
  headerMetaLine: { fontSize: 8, color: C.muted, marginTop: 2 },
  accentBar: { position: "absolute", top: 0, left: 0, right: 0, height: 4, backgroundColor: C.primary },

  legendRow: { flexDirection: "row", alignItems: "center", flexWrap: "wrap", marginBottom: 10 },
  legendLabel: { fontSize: 8, fontWeight: "bold", color: C.muted, marginRight: 10 },
  legendItem: { flexDirection: "row", alignItems: "center", marginRight: 14 },
  legendDot: { width: 6, height: 6, borderRadius: 3, marginRight: 4 },
  legendText: { fontSize: 8, color: C.dark },

  treeFrame: {
    flexGrow: 1,
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderColor: C.border,
    borderRadius: 6,
    padding: 8,
  },
  emptyText: { fontSize: 10, color: C.faint },

  section: { marginBottom: 18 },
  sectionTitle: {
    fontSize: 9,
    fontWeight: "bold",
    letterSpacing: 0.6,
    color: C.primary,
    paddingBottom: 5,
    marginBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: C.border,
  },
  cardRow: { flexDirection: "row", alignItems: "stretch" },
  card: {
    flexGrow: 1,
    flexBasis: 0,
    borderWidth: 1,
    borderColor: C.border,
    borderRadius: 6,
    backgroundColor: C.panel,
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  cardSpacer: { width: 10 },
  cardValue: { fontSize: 18, fontWeight: "bold", color: C.dark },
  cardLabel: { fontSize: 8.5, color: C.muted, marginTop: 3 },
  cardSub: { fontSize: 7.5, color: C.faint, marginTop: 2 },
  operator: { width: 22, fontSize: 16, color: C.faint, textAlign: "center", alignSelf: "center" },
  note: { fontSize: 8.5, color: C.muted, marginTop: 8 },

  footer: {
    position: "absolute",
    left: MARGIN.side,
    right: MARGIN.side,
    bottom: 18,
    flexDirection: "row",
    justifyContent: "space-between",
    borderTopWidth: 1,
    borderTopColor: C.border,
    paddingTop: 6,
  },
  footerText: { fontSize: 7.5, color: C.faint },
});

const formatCount = (value) => (value == null ? "—" : Number(value).toLocaleString("en-US"));

// Scales a captured tree to fit TREE_BOX without distorting it (and never
// upscales a small tree past its natural size, which would just blur it).
const fitToTreeBox = ({ width, height }) => {
  const scale = Math.min(TREE_BOX.width / width, TREE_BOX.height / height, 1);
  return { width: width * scale, height: height * scale };
};

const RunningHeader = ({ title, subtitle, meta }) => (
  <>
    <View style={styles.accentBar} fixed />
    <View style={styles.header}>
      <View>
        <Text style={styles.eyebrow}>ORGANISATION DESIGN REPORT</Text>
        <Text style={styles.title}>{title}</Text>
        {subtitle ? <Text style={styles.subtitle}>{subtitle}</Text> : null}
      </View>
      <View style={styles.headerMeta}>
        {meta.map((line) => (
          <Text key={line} style={styles.headerMetaLine}>{line}</Text>
        ))}
      </View>
    </View>
  </>
);

const Footer = () => (
  <View style={styles.footer} fixed>
    <Text style={styles.footerText}>{FOOTER_TEXT}</Text>
    <Text style={styles.footerText} render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`} />
  </View>
);

const Legend = ({ entryOption }) => (
  <View style={styles.legendRow}>
    <Text style={styles.legendLabel}>Role Changes</Text>
    {getLegendStatuses(entryOption).map((status) => {
      const config = getRoleChangeConfig(status, entryOption);
      return (
        <View key={status} style={styles.legendItem}>
          <View style={[styles.legendDot, { backgroundColor: SB_COLORS[config.accentToken] || C.faint }]} />
          <Text style={styles.legendText}>{config.label}</Text>
        </View>
      );
    })}
  </View>
);

const TreePage = ({ title, subtitle, meta, tree, entryOption, showLegend }) => (
  <Page size="A4" orientation="landscape" style={styles.page}>
    <RunningHeader title={title} subtitle={subtitle} meta={meta} />
    {showLegend && <Legend entryOption={entryOption} />}
    <View style={styles.treeFrame}>
      {tree ? (
        <Image src={tree.dataUrl} style={fitToTreeBox(tree)} />
      ) : (
        <Text style={styles.emptyText}>No roles to show.</Text>
      )}
    </View>
    <Footer />
  </Page>
);

const MetricCard = ({ value, label, sub, color }) => (
  <View style={styles.card}>
    <Text style={[styles.cardValue, color ? { color } : null]}>{value}</Text>
    <Text style={styles.cardLabel}>{label}</Text>
    {sub ? <Text style={styles.cardSub}>{sub}</Text> : null}
  </View>
);

// Interleaves a fixed-width gap (or an operator glyph) between cards —
// react-pdf's flex `gap` support is inconsistent across versions.
const CardRow = ({ children, operators }) => {
  const items = React.Children.toArray(children).filter(Boolean);
  return (
    <View style={styles.cardRow}>
      {items.map((child, i) => (
        <React.Fragment key={child.key ?? i}>
          {i > 0 && (operators ? <Text style={styles.operator}>{operators[i - 1]}</Text> : <View style={styles.cardSpacer} />)}
          {child}
        </React.Fragment>
      ))}
    </View>
  );
};

const SummaryPage = ({ meta, draft, canViewBudget }) => {
  const bridge = draft.stats ? getHeadcountBridge(draft.stats, draft.status_counts) : null;
  const roleCountCards = getRoleCountCards(draft.roleCounts, draft.entryOption);
  // Same calculation as the workspace Summary tab's Budget section.
  const { effectiveTarget, variance, isOverTarget } = getBudgetOverview(draft.baseline_budget, draft.targetBudget, draft.projected_budget);
  const varianceNote = getBudgetVarianceNote(variance);

  return (
    <Page size="A4" orientation="landscape" style={styles.page}>
      <RunningHeader title="Summary" subtitle="Key metrics for the proposed organisation design" meta={meta} />

      {bridge && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>HEADCOUNT BRIDGE</Text>
          <CardRow operators={["–", "+", "="]}>
            <MetricCard key="current" value={formatCount(bridge.currentFtes)} label="Current FTEs" />
            <MetricCard key="reductions" value={formatCount(bridge.reductions)} label="Reductions" sub={`from ${bridge.decreasedRoles} role(s)`} color={C.warning} />
            <MetricCard key="additions" value={formatCount(bridge.additions)} label="Additions" sub={`from ${bridge.addedRoles} role(s)`} color={C.success} />
            <MetricCard key="net" value={formatCount(bridge.netPositions)} label="Net Positions" color={C.primary} />
          </CardRow>
        </View>
      )}

      {roleCountCards.length > 0 && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>ROLE COUNT</Text>
          <CardRow>
            {roleCountCards.map(({ key, label, count, status }) => (
              <MetricCard
                key={key}
                value={formatCount(count)}
                label={label}
                color={status ? SB_COLORS[getRoleChangeConfig(status, draft.entryOption).accentToken] : undefined}
              />
            ))}
          </CardRow>
        </View>
      )}

      {canViewBudget && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>BUDGET OVERVIEW</Text>
          <CardRow>
            <MetricCard key="baseline" value={formatUsd(draft.baseline_budget)} label="Baseline Budget" />
            <MetricCard
              key="target"
              value={formatUsd(effectiveTarget)}
              label={draft.targetBudget == null ? "Target Budget (= baseline)" : "Target Budget"}
            />
            <MetricCard
              key="projected"
              value={formatUsd(draft.projected_budget)}
              label="Projected Budget"
              color={variance == null ? undefined : isOverTarget ? C.danger : C.success}
            />
          </CardRow>
          {varianceNote && <Text style={styles.note}>{varianceNote}</Text>}
        </View>
      )}

      <Footer />
    </Page>
  );
};

// Current + Proposed get a page each only when there's actually a
// difference to show. A fresh Current Org design (nothing edited yet) has
// identical before/after trees, so it collapses to one "Organisation
// Structure" page — same rule the on-screen Org View follows.
const OrgDesignReport = ({ draft, trees, meta, canViewBudget, hasProposedChanges }) => (
  <Document title={meta.title} author={meta.preparedBy || "SkillsBridge.AI"} creator="SkillsBridge.AI" producer="SkillsBridge.AI">
    {hasProposedChanges ? (
      <>
        <TreePage title="Current Organisation" subtitle="Organisation structure before proposed changes" meta={meta.lines} tree={trees.current} entryOption={draft.entryOption} />
        <TreePage
          title="Proposed Organisation"
          subtitle="Organisation structure with proposed changes applied"
          meta={meta.lines}
          tree={trees.proposed}
          entryOption={draft.entryOption}
          showLegend
        />
      </>
    ) : (
      <TreePage title="Organisation Structure" subtitle="Current organisation — no changes proposed yet" meta={meta.lines} tree={trees.current} entryOption={draft.entryOption} />
    )}
    <SummaryPage meta={meta.lines} draft={draft} canViewBudget={canViewBudget} />
  </Document>
);

// Captures a rendered tree exactly as the app draws it. The node lives in an
// off-screen stage (see PdfCaptureStageStyled); html2canvas measures the
// CLONE's position, so the clone's stage is pulled back on-screen first —
// otherwise the capture area sits off-canvas and comes out blank.
const captureTree = async (node) => {
  if (!node) return null;
  const width = node.scrollWidth;
  const height = node.scrollHeight;
  const scale = Math.min(
    MAX_CAPTURE_SCALE,
    MAX_CANVAS_SIDE / Math.max(width, height),
    Math.sqrt(MAX_CANVAS_AREA / (width * height))
  );
  const canvas = await html2canvas(node, {
    backgroundColor: "#ffffff",
    scale,
    logging: false,
    // The stage is position:fixed — a scrolled window would otherwise
    // offset the capture area and crop the tree.
    scrollX: 0,
    scrollY: 0,
    windowWidth: Math.max(window.innerWidth, width + 64),
    windowHeight: Math.max(window.innerHeight, height + 64),
    onclone: (doc) => {
      const stage = doc.querySelector("[data-org-pdf-stage]");
      if (stage) stage.style.left = "0px";
    },
  });
  return { dataUrl: canvas.toDataURL("image/png"), width, height };
};

const toFileSlug = (value) =>
  String(value)
    .replace(/[\\/:*?"<>|]+/g, "")
    .replace(/\s+/g, " ")
    .trim();

const triggerDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Revoked on the next tick — some browsers cancel the download if the
  // object URL disappears synchronously after click().
  setTimeout(() => URL.revokeObjectURL(url), 0);
};

/**
 * Builds the Organisation Design report (Current / Proposed / Summary, or
 * Organisation Structure / Summary when nothing is proposed yet — A4
 * landscape) and saves it straight to disk — no print dialog.
 */
export const downloadOrgDesignPdf = async ({ draft, currentTreeNode, proposedTreeNode, canViewBudget, hasProposedChanges, preparedBy }) => {
  // Sequential, not Promise.all — each html2canvas call clones the whole
  // document and allocates a full-size canvas; running both at once doubles
  // peak memory on exactly the large orgs most likely to hit browser limits.
  const current = await captureTree(currentTreeNode);
  const proposed = await captureTree(proposedTreeNode);

  const generatedAt = new Date();
  const dateLabel = generatedAt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  const versionLabel = draft.versionLabel || draft.sourceLabel;
  const title = versionLabel ? `Organisation Design — ${versionLabel}` : "Organisation Design";
  const lines = [
    versionLabel,
    BASELINE_TAG[draft.entryOption],
    preparedBy && `Prepared by ${preparedBy}`,
    `Generated ${dateLabel}`,
  ].filter(Boolean);

  const blob = await pdf(
    <OrgDesignReport
      draft={draft}
      trees={{ current, proposed }}
      meta={{ title, lines, preparedBy }}
      canViewBudget={canViewBudget}
      hasProposedChanges={hasProposedChanges}
    />
  ).toBlob();

  const isoDate = generatedAt.toISOString().slice(0, 10);
  triggerDownload(blob, `${toFileSlug(title)} - ${isoDate}.pdf`);
};
